import Pusher from 'pusher-js';
import Channel from 'pusher-js/types/src/core/channels/channel';
import Message from '@/models/MessageModel';
import { Options } from 'pusher-js/types/src/core/options';
import {
	CallbackParam,
	NewMessagePayload,
	StateChangePayload,
	PayloadFromServer
} from './interfaces';
import TriggerMessagePayload from '@/models/TriggerMessagePayloadModel';
import { getENV } from '@/helpers/Env';

type TransferMessages = (m: PayloadFromServer) => void;
type TransferStatus = (s: string) => void;

// Сингл тон для взаимодействия с пушером
export default class PusherService {
	private static instance: PusherService;

	private Pusher: Pusher;
	private channelStream!: Channel;
	private conversationId!: string;
	public transferMessages!: TransferMessages;
	public transferStatus!: TransferStatus;
	public state!: string;
	// 2126: уникальный токен вкладки для адресного канала Pusher (изоляция вкладок)
	private channelKey = '';
	// Адресный канал вкладки и признак, что подписка на него не удалась
	private tabChannel: Channel | null = null;
	private tabChannelFailed = false;
	// Сколько ждём подписку на адресный канал, прежде чем слать без channel_key
	private static readonly TAB_CHANNEL_WAIT_MS = 3000;

	constructor(
		conversationId: string,
		transferMessages: TransferMessages,
		transferStatus: TransferStatus,
		opt: Options
	) {
		if (PusherService.instance) {
			throw new Error('Error - use PusherService.getInstance()');
		}
		// Источник значений — /env.js стенда (window._env_), фоллбэк — hostname-derived
		// дефолты стенда: soketi за ws.<домен стенда>, TLS 443.
		const pusherEnv = getENV('VUE_APP_PUSHER') || 'soketi';
		const appKey = getENV('VUE_APP_PUSHER_KEY')!;
		const cluster = getENV('VUE_APP_PUSHER_CLUSTER');
		const proxy = getENV('VUE_APP_API_URL');
		const host = getENV('VUE_APP_PUSHER_HOST') || `ws.${window.location.hostname}`;
		const port = Number(getENV('VUE_APP_PUSHER_PORT') || 443);
		const tls = (getENV('VUE_APP_PUSHER_TLS') ?? 'true') !== 'false';

		if (pusherEnv == 'pusher') {
			opt = {
				cluster,
				authEndpoint: proxy ? `${proxy}/pusher/auth/private` : '/pusher/auth/private'
			};
		} else {
			opt = {
				wsHost: host,
				wsPort: port,
				wssPort: port,
				forceTLS: tls,
				disableStats: true,
				enabledTransports: ['ws', 'wss'],
				authEndpoint: proxy ? `${proxy}/pusher/auth/private` : '/pusher/auth/private'
			};
		}

		this.Pusher = new Pusher(appKey, opt);

		// 2126: токен вкладки (sessionStorage) — уникален на вкладку, переживает её перезагрузку,
		// но не делится между вкладками. Нужен для адресного канала, чтобы ответ пришёл в ту же вкладку.
		this.channelKey = PusherService.resolveChannelKey();

		// И сразу же подписываемся на диалог
		this.subscribeToConversation(conversationId, transferMessages, transferStatus);
	}

	// 2126: получить/создать токен вкладки в sessionStorage.
	// sessionStorage изолирован по вкладке и переживает перезагрузку той же вкладки.
	private static resolveChannelKey(): string {
		const STORAGE_KEY = 'bb_channel_key';
		try {
			const existing = sessionStorage.getItem(STORAGE_KEY);
			if (existing) {
				return existing;
			}
			// crypto.randomUUID есть не во всех типах/окружениях (TS<4.4) — берём через any + фоллбэк
			const cryptoObj: any = typeof crypto !== 'undefined' ? crypto : undefined;
			const key: string =
				cryptoObj && typeof cryptoObj.randomUUID === 'function'
					? cryptoObj.randomUUID()
					: `${Date.now()}-${Math.random().toString(16).slice(2)}`;
			sessionStorage.setItem(STORAGE_KEY, key);
			return key;
		} catch (e) {
			// sessionStorage недоступен (приватный режим и т.п.) — работаем без адресного канала.
			return '';
		}
	}

	// transferMessages - Функция которая прокидывает новое сообщение в состояние внутри компонента
	// transferStatus - Функция которая прокидывает статус внутрь компонента
	static getInstance(
		conversationId: string,
		transferMessages: TransferMessages,
		transferStatus: TransferStatus,
		opt: Options
	): PusherService {
		PusherService.instance =
			PusherService.instance ||
			new PusherService(conversationId, transferMessages, transferStatus, opt);
		return PusherService.instance;
	}

	private subscribeToConversation(
		conversationId: string,
		transferMessages: TransferMessages,
		transferStatus: TransferStatus
	) {
		if (this.isSubscribed) return;

		this.transferMessages = transferMessages;
		this.transferStatus = transferStatus;
		this.conversationId = conversationId;

		const handleMessage = (p: CallbackParam) => {
			this.transferMessages(p.message);
		};

		this.channelStream = this.Pusher.subscribe(
			`private-Webbot.Conversation.${conversationId}`
		);
		this.channelStream.bind('App\\Events\\Webbot\\MessageWasSended', handleMessage);

		// 2126: адресный канал вкладки — сюда бэк шлёт ответы именно этой вкладке.
		// Обычный канал оставляем для async/легаси-сообщений и обратной совместимости.
		if (this.channelKey) {
			this.tabChannel = this.Pusher.subscribe(
				`private-Webbot.Conversation.${conversationId}.${this.channelKey}`
			);
			this.tabChannel.bind('App\\Events\\Webbot\\MessageWasSended', handleMessage);
			this.tabChannel.bind('pusher:subscription_error', () => {
				this.tabChannelFailed = true;
			});
		}

		this.subscribeOnStateChange();
	}

	private subscribeOnStateChange(): void {
		this.Pusher.connection.bind('state_change', (states: StateChangePayload) => {
			this.state = states.current;
			this.transferStatus(this.state);
		});
	}

	public sendMessage(payload: NewMessagePayload, buttonType: string | null = null) {
		if (buttonType !== 'action') {
			this.setMyAnswerInMessages(payload);
			this.setDelayMessage();
		}

		// А потом одаем его на сервер
		this.sendMessageOnServer(
			{
				...payload,
				text: btoa(unescape(encodeURIComponent(payload.text))),
				text_body: btoa(unescape(encodeURIComponent(payload.text))),
				conversation_id: this.conversationId
			},
			'client-MessageWasSended'
		);
	}

	// Рекурсивная функция, через которую пытаемся отправить на сервер
	private sendMessageOnServer(
		message: NewMessagePayload | TriggerMessagePayload,
		triggerName: string,
		startedAt: number = Date.now()
	) {
		if (!this.isSubscribed) {
			return setTimeout(
				() => this.sendMessageOnServer(message, triggerName, startedAt),
				500
			);
		}

		// При таком варианте чуть дольше ожидаем прежде чем вызвать рекурсивно еще раз
		if (this.state !== 'connected') {
			return setTimeout(
				() => this.sendMessageOnServer(message, triggerName, startedAt),
				1500
			);
		}

		// С channel_key бэк отвечает только в адресный канал: если вкладка на него ещё
		// не подписана, Pusher выбросит ответ и бот «зависнет». Ждём подписку, а не
		// дождавшись — шлём без channel_key, тогда ответ придёт в общий канал.
		if (
			this.isTabChannelPending &&
			Date.now() - startedAt < PusherService.TAB_CHANNEL_WAIT_MS
		) {
			return setTimeout(
				() => this.sendMessageOnServer(message, triggerName, startedAt),
				100
			);
		}

		this.channelStream.trigger(triggerName, {
			// 2126: прокидываем токен вкладки — бэк вернёт ответ на адресный канал этой вкладки
			message: this.isTabChannelReady
				? { ...message, channel_key: this.channelKey }
				: message
		});
	}

	private get isTabChannelReady(): boolean {
		return !!this.channelKey && !!this.tabChannel?.subscribed;
	}

	private get isTabChannelPending(): boolean {
		return !!this.channelKey && !this.tabChannelFailed && !this.tabChannel?.subscribed;
	}

	// Отправляем событие
	public sendTrigger(payload: TriggerMessagePayload) {
		this.sendMessageOnServer(payload, 'client-TriggerEventWasFired');
	}

	public get isConnected(): boolean {
		return this.channelStream?.pusher.connection.state === 'connected';
	}

	public get isSubscribed(): boolean {
		return this.channelStream?.subscribed;
	}

	private setDelayMessage(): void {
		this.transferMessages({
			type: 'action_typing'
		});
	}

	// Устанавливаем ответ в массив сообщений
	private setMyAnswerInMessages(message: Message): void {
		// Если у нас команда или геолокация, то корректное сообщение
		// с кнопки хранится в text_body
		let text;
		if (message.type === 'command' || message.type === 'location') {
			text = message.text_body;
		} else {
			text = message.text;
		}

		this.transferMessages({
			text,
			type: 'text-my'
		});

		// И в случае гео локации мы должны сами сделать ответ от бота в ввиде
		// координат + картинка
		if (message.type === 'location') {
			this.setGeoLocationMessage(message);
		}
	}

	// Когда отправляем гео локацию - то здесь устанавливаем как будто ответ с сервера пришле с картинкой

	private setGeoLocationMessage(message: Message) {
		if (!message.text) {
			return;
		}

		const coords = message.text.split(',');

		const image =
			'https://maps.googleapis.com/maps/api/staticmap?center=' +
			coords[0] +
			'&markers=color:red%7Clabel:C%7C' +
			coords[1] +
			// TODO: Не действительный ключ
			'&zoom=14&size=300x210&key=AIzaSyBLLCPvj6o-4p-oPAMbC9svd26cl3R1TCM';

		this.transferMessages({
			text: message.text,
			type: 'text-bot',
			image
		});
	}
}
